import React, { useEffect, useRef, useState } from 'react';
import Button from '@/components/UI/Button';
import type { StepProps } from '../types';

// Challenge display names
const CHALLENGE_DISPLAY_NAMES: Record<string, string> = {
  'multiple-programs': 'Managing multiple programs across different systems',
  'tracking-budgets': 'Tracking project budgets and expenditures',
  visibility: 'Lack of visibility into project status and timelines',
  'manual-data': 'Manual data entry and reporting processes',
  'compliance-docs': 'Difficulty maintaining compliance documentation',
  'inconsistent-execution': 'Inconsistent project execution across teams',
  'track-outcomes': 'Limited ability to track program outcomes/impact',
  'poor-communication': 'Poor communication between stakeholders',
  'vendor-management': 'Vendor management and coordination challenges',
  'intake-bottlenecks': 'Application intake and processing bottlenecks',
};

// Video mapping without extensions
const VIDEO_MAPPING: Record<string, string> = {
  'multiple-programs': 'multiple-programs',
  'tracking-budgets': 'tracking-budgets--visibility',
  visibility: 'tracking-budgets--visibility',
  'manual-data': 'manual-data--compliance-docs--poor-communication',
  'compliance-docs': 'manual-data--compliance-docs--poor-communication',
  'poor-communication': 'manual-data--compliance-docs--poor-communication',
  'inconsistent-execution': 'inconsistent-execution--track-outcomes--vendor-management--intake-bottlenecks',
  'track-outcomes': 'inconsistent-execution--track-outcomes--vendor-management--intake-bottlenecks',
  'vendor-management': 'inconsistent-execution--track-outcomes--vendor-management--intake-bottlenecks',
  'intake-bottlenecks': 'inconsistent-execution--track-outcomes--vendor-management--intake-bottlenecks',
};

// Group challenges by video
const _CHALLENGE_GROUPS: Record<string, string[]> = {
  'multiple-programs': ['multiple-programs'],
  'tracking-budgets--visibility': ['tracking-budgets', 'visibility'],
  'manual-data--compliance-docs--poor-communication': ['manual-data', 'compliance-docs', 'poor-communication'],
  'inconsistent-execution--track-outcomes--vendor-management--intake-bottlenecks': [
    'inconsistent-execution',
    'track-outcomes',
    'vendor-management',
    'intake-bottlenecks',
  ],
};

const PERMANENT_VIDEOS = {
  welcome: {
    key: 'welcome',
    title: 'Welcome to SEIN Terminal',
    description: 'Introduction to SEIN Terminal and its capabilities',
  },
  nextSteps: {
    key: 'next-steps',
    title: 'Next Steps',
    description: 'What to expect after implementing SEIN Terminal',
  },
};

const S3_BASE_URL = 'https://stp-public-marketing-videos.s3.us-east-2.amazonaws.com';

// Add this context at the top level of the file
const VideoContext = React.createContext<{
  currentlyPlaying: string | null;
  setCurrentlyPlaying: (key: string | null) => void;
}>({
  currentlyPlaying: null,
  setCurrentlyPlaying: () => {},
});

// Add Thumbnail component
const Thumbnail = React.memo<{
  videoKey: string;
  displayTitle: string;
  onClick: () => void;
}>(({ videoKey, displayTitle, onClick }) => {
  const thumbnailUrl = `${S3_BASE_URL}/${videoKey}-thumbnail.png`;

  return (
    <div className='relative w-full aspect-video bg-gray-100 rounded-lg cursor-pointer group' onClick={onClick}>
      <img
        src={thumbnailUrl}
        alt={`Thumbnail for ${displayTitle}`}
        className='w-full h-full object-cover rounded-lg'
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
      <div className='absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-opacity'>
        <svg className='w-16 h-16 text-white' fill='currentColor' viewBox='0 0 20 20'>
          <path d='M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
        </svg>
      </div>
    </div>
  );
});

Thumbnail.displayName = 'Thumbnail';

const VideoPlayer: React.FC<{
  videoKey: string;
  title?: string;
  description?: string;
  relatedChallenges?: string[];
}> = ({ videoKey, title, description, relatedChallenges }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { currentlyPlaying, setCurrentlyPlaying } = React.useContext(VideoContext);
  const videoUrl = `${S3_BASE_URL}/${videoKey}.mp4`;
  const displayTitle = title || CHALLENGE_DISPLAY_NAMES[videoKey];

  // Handle video play/pause
  const handlePlay = () => {
    if (currentlyPlaying && currentlyPlaying !== videoKey) {
      // Pause other videos
      const otherVideo = document.querySelector(`video[data-key="${currentlyPlaying}"]`) as HTMLVideoElement;
      if (otherVideo) {
        otherVideo.pause();
      }
    }
    setCurrentlyPlaying(videoKey);
    setIsPlaying(true);
  };

  const handlePause = () => {
    setCurrentlyPlaying(null);
    setIsPlaying(false);
  };

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (currentlyPlaying === videoKey) {
        setCurrentlyPlaying(null);
      }
    };
  }, [videoKey, currentlyPlaying, setCurrentlyPlaying]);

  return (
    <div className='space-y-3'>
      {(displayTitle || description) && (
        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>{displayTitle}</h3>
          {description && <p className='text-sm text-gray-700'>{description}</p>}
          {relatedChallenges && relatedChallenges.length > 1 && (
            <div className='mt-2'>
              <p className='text-sm text-gray-600'>
                This video also covers: {relatedChallenges.map((challenge) => CHALLENGE_DISPLAY_NAMES[challenge]).join(', ')}
              </p>
            </div>
          )}
        </div>
      )}

      {!isPlaying ? (
        <Thumbnail videoKey={videoKey} displayTitle={displayTitle} onClick={handlePlay} />
      ) : (
        <div className='w-full aspect-video bg-black rounded-lg overflow-hidden'>
          <video
            ref={videoRef}
            data-key={videoKey}
            src={videoUrl}
            controls
            autoPlay
            className='w-full h-full'
            onEnded={handlePause}
            onPause={handlePause}
            onPlay={handlePlay}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

const SENTerminalSolutions: React.FC<StepProps> = ({ onNext }) => {
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);

  useEffect(() => {
    // Get selected challenges from localStorage
    const challenges = JSON.parse(localStorage.getItem('selectedChallenges') || '[]') as string[];
    setSelectedChallenges(challenges);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  // Get unique video URLs for selected challenges
  const getUniqueVideos = () => {
    const uniqueVideos = new Map<string, string[]>();

    // biome-ignore lint/complexity/noForEach: <explanation>
    selectedChallenges.forEach((challenge) => {
      const videoKey = VIDEO_MAPPING[challenge];
      if (videoKey) {
        if (!uniqueVideos.has(videoKey)) {
          uniqueVideos.set(videoKey, [challenge]);
        } else {
          uniqueVideos.get(videoKey)?.push(challenge);
        }
      }
    });

    return Array.from(uniqueVideos.entries()).map(([videoKey, challenges]) => ({
      videoKey,
      challenges,
      primaryChallenge: challenges[0],
    }));
  };

  return (
    <VideoContext.Provider value={{ currentlyPlaying, setCurrentlyPlaying }}>
      <div className='bg-white shadow-sm rounded-lg'>
        <div className='p-4 sm:p-6'>
          <h2 className='text-xl font-medium text-green-600 mb-6'>SEIN Terminal Solutions</h2>

          <form onSubmit={handleSubmit}>
            <div className='space-y-6'>
              <div className='space-y-8'>
                {/* Welcome Video */}
                <div className='space-y-3'>
                  <VideoPlayer
                    videoKey={PERMANENT_VIDEOS.welcome.key}
                    title={PERMANENT_VIDEOS.welcome.title}
                    description={PERMANENT_VIDEOS.welcome.description}
                  />
                </div>

                {/* Selected Challenge Videos */}
                {selectedChallenges.length > 0 ? (
                  getUniqueVideos().map(({ videoKey, challenges, primaryChallenge }) => (
                    <div key={videoKey} className='space-y-3'>
                      <VideoPlayer
                        videoKey={videoKey}
                        title={CHALLENGE_DISPLAY_NAMES[primaryChallenge]}
                        relatedChallenges={challenges.length > 1 ? challenges : undefined}
                      />
                      <div className='flex items-center justify-end text-sm text-gray-500'>
                        <a
                          href={`${S3_BASE_URL}/${videoKey}.mp4`}
                          download
                          className='text-green-600 hover:text-green-700 flex items-center space-x-1'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                            />
                          </svg>
                          <span>Download Video</span>
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center'>
                    <p className='text-gray-500'>No challenges selected. Please select challenges to view solutions.</p>
                  </div>
                )}

                {/* Next Steps Video */}
                <div className='space-y-3'>
                  <VideoPlayer
                    videoKey={PERMANENT_VIDEOS.nextSteps.key}
                    title={PERMANENT_VIDEOS.nextSteps.title}
                    description={PERMANENT_VIDEOS.nextSteps.description}
                  />
                </div>
              </div>

              <div className='flex justify-end pt-6'>
                <Button type='submit' btnStyle='btn-primary' label='Next' customClassName='w-full sm:w-auto' />
              </div>
            </div>
          </form>
        </div>
      </div>
    </VideoContext.Provider>
  );
};

export default SENTerminalSolutions;
